import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Users, Car, Crown, Check, ChevronsUpDown, Search, Plane, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTransfers, TransferRoute, TransferLocation } from '@/hooks/useTransfers';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import { useTranslatedText } from '@/hooks/useTranslatedText';
import sedanImage from '@/assets/sedan-transfer.jpg';
import suvImage from '@/assets/suv-transfer.jpg';

export interface TransferSearchResultData {
  selectedRoute: TransferRoute;
  passengers: number;
  tripType: 'oneway' | 'roundtrip';
  calculatePrice: (route: TransferRoute, passengers: number, serviceType: 'standard' | 'vip', tripType: 'oneway' | 'roundtrip') => number;
  fromName: string;
  toName: string;
}

interface TransferBookingFormProps {
  preselectedDestination?: string;
  onResultsFound?: (data: TransferSearchResultData) => void;
  hideResults?: boolean;
}

export const TransferBookingForm = ({ preselectedDestination, onResultsFound, hideResults }: TransferBookingFormProps = {}) => {
  const navigate = useNavigate();
  const {
    allLocations,
    routes,
    loading,
    availableDestinations,
    searchTransferPricing,
    calculatePrice,
    getAvailableDestinations,
    getAllFromLocations,
    configLoaded
  } = useTransfers();
  const { getVehicleByPassengers } = useVehicleImages();
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [tripType, setTripType] = useState<'oneway' | 'roundtrip'>('oneway');
  const [selectedRoute, setSelectedRoute] = useState<TransferRoute | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [openFromCombo, setOpenFromCombo] = useState(false);
  const [openToCombo, setOpenToCombo] = useState(false);
  const [openPassengerCombo, setOpenPassengerCombo] = useState(false);
  const [fromLocations, setFromLocations] = useState<TransferLocation[]>([]);

  useEffect(() => {
    const loadFromLocations = async () => {
      const locations = await getAllFromLocations();
      setFromLocations(locations);
      if (preselectedDestination && locations.length > 0) {
        const matchingLocation = locations.find(
          loc => loc.name.toLowerCase() === preselectedDestination.toLowerCase()
        );
        if (matchingLocation) {
          setToLocation(matchingLocation.id);
        }
      }
    };
    loadFromLocations();
    const handleFocus = () => { loadFromLocations(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [preselectedDestination, configLoaded]);

  useEffect(() => {
    if (passengers > 5 && showPricing) {
      if (selectedRoute) setShowPricing(true);
    }
  }, [passengers, showPricing, selectedRoute]);

  const handleSearch = async () => {
    if (!fromLocation || !toLocation) return;
    const results = await searchTransferPricing(fromLocation, toLocation);
    if (results.length > 0) {
      setSelectedRoute(results[0]);
      setShowPricing(true);
      if (onResultsFound) {
        const fromName = [...fromLocations, ...allLocations].find(l => l.id === fromLocation)?.name || '';
        const toName = [...fromLocations, ...allLocations, ...availableDestinations].find(l => l.id === toLocation)?.name || '';
        onResultsFound({
          selectedRoute: results[0],
          passengers,
          tripType,
          calculatePrice,
          fromName,
          toName,
        });
      }
    }
  };

  useEffect(() => {
    if (fromLocation) handleFromLocationChange(fromLocation);
  }, [fromLocation]);

  const handleFromLocationChange = async (locationId: string) => {
    const fromLocationObj = [...fromLocations, ...allLocations].find(l => l.id === locationId);
    if (!fromLocationObj) return;
    if (fromLocationObj.type === 'hotel' && fromLocationObj.airportZone) {
      const correspondingAirport = [...fromLocations, ...allLocations].find(l => l.type === 'airport' && l.tableName === fromLocationObj.airportZone);
      if (correspondingAirport) setToLocation(correspondingAirport.id);
    } else {
      setToLocation('');
    }
    await getAvailableDestinations(locationId);
    setShowPricing(false);
    setSelectedRoute(null);
  };

  const handleServiceSelect = (serviceType: 'standard' | 'vip') => {
    if (!selectedRoute) return;
    const price = calculatePrice(selectedRoute, passengers, serviceType, tripType);
    navigate('/booking', {
      state: {
        from: [...fromLocations, ...allLocations].find(l => l.id === fromLocation)?.name,
        to: [...fromLocations, ...allLocations, ...availableDestinations].find(l => l.id === toLocation)?.name,
        passengers,
        tripType,
        serviceType,
        price,
        route: selectedRoute
      }
    });
  };

  return (
    <div className="w-full">
      {/* Main Form Card */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-foreground mb-5">Book Your Transfer</h3>

        {/* Trip Type Tabs */}
        <div className="flex border-b border-border mb-6">
          <button
            onClick={() => setTripType('oneway')}
            className={cn(
              "pb-3 px-4 text-sm font-semibold transition-all border-b-2 -mb-px",
              tripType === 'oneway'
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            One Way
          </button>
          <button
            onClick={() => setTripType('roundtrip')}
            className={cn(
              "pb-3 px-4 text-sm font-semibold transition-all border-b-2 -mb-px",
              tripType === 'roundtrip'
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Round Trip
          </button>
        </div>

        {/* Form Fields - Stacked */}
        <div className="space-y-4">
          {/* From Location */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              Pick-up Location
            </label>
            <Popover open={openFromCombo} onOpenChange={setOpenFromCombo}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between h-12 text-sm border bg-white hover:bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Plane className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="truncate">
                      {fromLocation ? allLocations.find(l => l.id === fromLocation)?.name : "Select pickup..."}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border shadow-xl z-50">
                <Command>
                  <CommandInput placeholder="Search locations..." />
                  <CommandList>
                    <CommandEmpty>No location found.</CommandEmpty>
                    <CommandGroup heading="Airports">
                      {fromLocations.filter(l => l.type === 'airport').map(location => (
                        <CommandItem key={location.id} value={location.name} onSelect={() => { setFromLocation(location.id); setOpenFromCombo(false); }} className="cursor-pointer">
                          <Check className={cn("mr-2 h-4 w-4", fromLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                          <MapPin className="mr-2 h-4 w-4 text-primary" />
                          {location.name}
                          <Badge variant="default" className="ml-auto text-[10px]">Airport</Badge>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Hotels">
                      {fromLocations.filter(l => l.type === 'hotel').map(location => (
                        <CommandItem key={location.id} value={location.name} onSelect={() => { setFromLocation(location.id); setOpenFromCombo(false); }} className="cursor-pointer">
                          <Check className={cn("mr-2 h-4 w-4", fromLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                          <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                          {location.name}
                          <Badge variant="secondary" className="ml-auto text-[10px]">Hotel</Badge>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* To Location */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              Drop Off
            </label>
            <Popover open={openToCombo} onOpenChange={setOpenToCombo}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" disabled={!fromLocation || availableDestinations.length === 0} className="w-full justify-between h-12 text-sm border bg-white hover:bg-muted/30 disabled:opacity-50">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-secondary flex-shrink-0" />
                    <span className="truncate">
                      {toLocation ? [...fromLocations, ...allLocations, ...availableDestinations].find(l => l.id === toLocation)?.name : availableDestinations.length === 0 ? "Select pickup first..." : "Select destination..."}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border shadow-xl z-50">
                <Command>
                  <CommandInput placeholder="Search destinations..." />
                  <CommandList>
                    <CommandEmpty>No destination found.</CommandEmpty>
                    <CommandGroup>
                      {availableDestinations.map(location => (
                        <CommandItem key={location.id} value={location.name} onSelect={() => { setToLocation(location.id); setOpenToCombo(false); }} className="cursor-pointer">
                          <Check className={cn("mr-2 h-4 w-4", toLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                          <MapPin className="mr-2 h-4 w-4" />
                          {location.name}
                          <Badge variant={location.type === 'airport' ? 'default' : 'secondary'} className="ml-auto text-[10px]">
                            {location.type}
                          </Badge>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Passengers */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              Passengers
            </label>
            <Popover open={openPassengerCombo} onOpenChange={setOpenPassengerCombo}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between h-12 text-sm border bg-white hover:bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{passengers} {passengers === 1 ? 'Passenger' : 'Passengers'}</span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0 bg-white border shadow-xl z-50">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {[...Array(12)].map((_, i) => (
                        <CommandItem key={i + 1} value={`${i + 1} passengers`} onSelect={() => { setPassengers(i + 1); setOpenPassengerCombo(false); }} className="cursor-pointer">
                          <Check className={cn("mr-2 h-4 w-4", passengers === i + 1 ? "opacity-100 text-primary" : "opacity-0")} />
                          <Users className="mr-2 h-4 w-4" />
                          {i + 1} {i === 0 ? 'Passenger' : 'Passengers'}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            disabled={!fromLocation || !toLocation || loading}
            className="w-full h-12 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            <Search className="mr-2 h-5 w-5" />
            {loading ? 'Searching...' : 'Search Available Transfers'}
          </Button>
        </div>
      </div>

      {/* Results Section - only shown when hideResults is not true */}
      {!hideResults && showPricing && selectedRoute && (
        <div className="mt-8 animate-fade-in">
          <h3 className="text-2xl font-bold mb-2">Available Services</h3>
          <p className="text-muted-foreground mb-6 text-sm">Choose the perfect service for your journey</p>

          <div className="grid md:grid-cols-2 gap-6">
            {(() => {
              const standardVehicle = getVehicleByPassengers('standard', passengers);
              return (
                <div className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img src={standardVehicle?.image_url || sedanImage} alt="Standard Transfer" className="w-full h-full object-cover" />
                    <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs">Standard</Badge>
                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 px-2.5 py-1 rounded-full text-xs font-medium">
                      <Users className="h-3 w-3 text-primary" />
                      {passengers} pax
                    </div>
                  </div>
                  <div className="p-5">
                    <h4 className="text-lg font-bold mb-2">Comfortable Ride</h4>
                    <p className="text-muted-foreground text-sm mb-3">
                      {standardVehicle?.description || "Comfortable sedan or similar"}
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {(standardVehicle?.features || ['Professional driver', 'Air conditioning', 'Luggage included']).map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-muted-foreground text-sm">
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-3xl font-bold text-primary">
                        ${calculatePrice(selectedRoute, passengers, 'standard', tripType).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">{tripType === 'roundtrip' ? 'Round trip' : 'One way'}</p>
                    </div>
                    <Button onClick={() => handleServiceSelect('standard')} className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground">
                      Select Service
                    </Button>
                  </div>
                </div>
              );
            })()}

            {(() => {
              const vipVehicle = getVehicleByPassengers('vip', passengers);
              if (!vipVehicle) return null;
              return (
                <div className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img src={vipVehicle.image_url || suvImage} alt="VIP Transfer" className="w-full h-full object-cover" />
                    <Badge className="absolute top-3 left-3 bg-secondary text-secondary-foreground text-xs flex items-center gap-1">
                      <Crown className="h-3 w-3" /> VIP
                    </Badge>
                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 px-2.5 py-1 rounded-full text-xs font-medium">
                      <Users className="h-3 w-3 text-secondary" />
                      {passengers} pax
                    </div>
                  </div>
                  <div className="p-5">
                    <h4 className="text-lg font-bold mb-2">Luxury Experience</h4>
                    <p className="text-muted-foreground text-sm mb-3">
                      {vipVehicle.description || "Luxury SUV or similar"}
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {vipVehicle.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-muted-foreground text-sm">
                          <Check className="h-4 w-4 text-secondary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-3xl font-bold text-secondary">
                        ${calculatePrice(selectedRoute, passengers, 'vip', tripType).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">{tripType === 'roundtrip' ? 'Round trip' : 'One way'}</p>
                    </div>
                    <Button onClick={() => handleServiceSelect('vip')} className="w-full h-11 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                      Select Service
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
